import { motion } from 'framer-motion'
import { Play, ExternalLink, Bell, Youtube, Radio } from 'lucide-react'

// YouTube Channel/Playlist Configuration
const YOUTUBE_CONFIG = {
  playlistId: 'PLhUSSHKj-nG_f2IOOj6Au917Juj0lVVcl',
  playlistUrl: 'https://www.youtube.com/playlist?list=PLhUSSHKj-nG_f2IOOj6Au917Juj0lVVcl',
  // Subscribe URL - links to playlist where users can subscribe
  subscribeUrl: 'https://www.youtube.com/playlist?list=PLhUSSHKj-nG_f2IOOj6Au917Juj0lVVcl',
}

const Sermons = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <motion.span
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20"
                whileHover={{ scale: 1.05 }}
              >
                <Radio className="w-4 h-4 text-red-400 animate-pulse" />
                Watch Live & On Demand
              </motion.span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
                Sermons & <span className="bg-gradient-to-r from-accent-400 to-orange-400 bg-clip-text text-transparent">Live Worship</span>
              </h1>
              <p className="text-lg text-white/70 mb-8">
                Join us for live services or catch up on past messages. Subscribe to our YouTube channel to never miss a service.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4">
                <motion.a
                  href={YOUTUBE_CONFIG.subscribeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-600/30 transition-all"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Youtube className="w-6 h-6" />
                  Subscribe on YouTube
                  <Bell className="w-5 h-5" />
                </motion.a>
                <motion.a
                  href={YOUTUBE_CONFIG.playlistUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-2xl bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold border border-white/20 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Play className="w-5 h-5" fill="currentColor" />
                  View All Videos
                  <ExternalLink className="w-4 h-4" />
                </motion.a>
              </div>
            </motion.div>

            {/* Right Column - Live/Latest Video Embed */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10">
                {/* YouTube Playlist Embed - Shows latest video first */}
                <iframe
                  src={`https://www.youtube.com/embed/videoseries?list=${YOUTUBE_CONFIG.playlistId}&autoplay=0&rel=0`}
                  title="DLBC Clarksville - Live & Recent Services"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-accent-500 to-orange-500 rounded-2xl -z-10 opacity-60 blur-xl" />
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl -z-10 opacity-60 blur-xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* YouTube Playlist Section */}
      <section className="py-20 bg-gradient-to-b from-dark to-dark-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Watch Previous Services
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Browse through our complete sermon library. Click any video to watch, or use the playlist navigation to find specific messages.
            </p>
          </motion.div>

          {/* Full Playlist Embed */}
          <motion.div
            className="relative max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="aspect-video rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10 bg-dark">
              <iframe
                src={`https://www.youtube.com/embed/videoseries?list=${YOUTUBE_CONFIG.playlistId}&rel=0`}
                title="DLBC Clarksville - Sermon Playlist"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>

            {/* Subscribe CTA below player */}
            <motion.div
              className="mt-10 text-center"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-white/60 mb-5">Never miss a service - Subscribe and turn on notifications!</p>
              <motion.a
                href={YOUTUBE_CONFIG.subscribeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-600/25 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Youtube className="w-6 h-6" />
                Subscribe to Our Channel
                <Bell className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-b from-dark-100 to-light-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mb-6">
              Join Us for Worship
            </h2>
            <p className="text-lg text-dark-300 mb-8 max-w-2xl mx-auto">
              We'd love to have you join us in person! Our services are held every Sunday.
              Come experience the warmth of our community and the power of God's Word.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <motion.a
                href="/about"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-primary-600 hover:bg-primary-700 text-white font-bold shadow-lg shadow-primary-600/25 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn About Us
              </motion.a>
              <motion.a
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl border-2 border-dark-200 text-dark-100 font-bold hover:bg-dark-100 hover:text-white transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Directions
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default Sermons
